import axios, { AxiosInstance, Method } from 'axios';
import FormData from 'form-data';
import { VoniSigner } from './signer';
import { VoniAuthError, VoniApiError } from './exceptions';
import { ProjectResource } from './resources/projects';
import { ChatResource } from './resources/chat';
import { WebhookResource } from './resources/webhooks';
import { ConversationResource } from './resources/conversations';
import { MessageResource } from './resources/messages';
import { DocumentResource } from './resources/documents';
import { UserResource } from './resources/users';
import { AnalyticsResource } from './resources/analytics';
import { AppointmentResource } from './resources/appointments';
import { LeadResource } from './resources/leads';
import { PromptTemplateResource } from './resources/prompt-templates';
import { IntegrationResource } from './resources/integrations';
import { CaptchaResource } from './resources/captcha';
import { InvitationResource } from './resources/invitations';
import { ApiKeysResource } from './resources/api-keys';
import { ConflictsResource } from './resources/conflicts';
import { ProjectMembersResource } from './resources/project-members';
import { LogsResource } from './resources/logs';
import { SystemResource } from './resources/system';
import { ContactResource } from './resources/contact';


export interface VoniConfig {
  apiKey?: string;
  apiUrl?: string;
  timeout?: number;
}

export class Voni {
  private apiKey?: string;
  public readonly apiUrl: string;
  private client: AxiosInstance;

  public projects: ProjectResource;
  public chat: ChatResource;
  public webhooks: WebhookResource;
  public conversations: ConversationResource;
  public messages: MessageResource;
  public documents: DocumentResource;
  public users: UserResource;
  public analytics: AnalyticsResource;
  public appointments: AppointmentResource;
  public leads: LeadResource;
  public promptTemplates: PromptTemplateResource;
  public integrations: IntegrationResource;
  public captcha: CaptchaResource;
  public invitations: InvitationResource;
  public apiKeys: ApiKeysResource;
  public conflicts: ConflictsResource;
  public projectMembers: ProjectMembersResource;
  public logs: LogsResource;
  public system: SystemResource;
  public contact: ContactResource;

  constructor(config: VoniConfig) {
    const envApiKey = typeof process !== 'undefined' ? process.env?.VONI_API_KEY : undefined;
    this.apiKey = config.apiKey || envApiKey;
    this.apiUrl = (config.apiUrl || 'https://api.voni.dev').replace(/\/$/, '');

    this.client = axios.create({
      baseURL: this.apiUrl,
      timeout: config.timeout || 30000,
    });

    this.projects = new ProjectResource(this);
    this.chat = new ChatResource(this);
    this.webhooks = new WebhookResource(this);
    this.conversations = new ConversationResource(this);
    this.messages = new MessageResource(this);
    this.documents = new DocumentResource(this);
    this.users = new UserResource(this);
    this.analytics = new AnalyticsResource(this);
    this.appointments = new AppointmentResource(this);
    this.leads = new LeadResource(this);
    this.promptTemplates = new PromptTemplateResource(this);
    this.integrations = new IntegrationResource(this);
    this.captcha = new CaptchaResource(this);
    this.invitations = new InvitationResource(this);
    this.apiKeys = new ApiKeysResource(this);
    this.conflicts = new ConflictsResource(this);
    this.projectMembers = new ProjectMembersResource(this);
    this.logs = new LogsResource(this);
    this.system = new SystemResource(this);
    this.contact = new ContactResource(this);
  }

  async request<T>(
    method: Method,
    path: string,
    params?: Record<string, unknown>,
    data?: unknown
  ): Promise<T> {
    const urlPath = `/api/v1${path}`;
    const timestamp = Math.floor(Date.now() / 1000).toString();

    const isFormData = data instanceof FormData;
    const bodyStr = data && !isFormData ? JSON.stringify(data) : '';

    const headers: Record<string, string> = {};

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    } else {
      const formHeaders = (data as FormData).getHeaders();
      Object.assign(headers, formHeaders);
    }

    if (this.apiKey) {
      const signature = VoniSigner.signRequest(
        this.apiKey,
        method as string,
        urlPath,
        bodyStr,
        parseInt(timestamp)
      );

      headers['X-API-Key'] = this.apiKey;
      headers['X-Voni-Timestamp'] = timestamp;
      headers['X-Voni-Signature'] = signature;
    }

    try {
      const response = await this.client.request({
        method,
        url: urlPath,
        params,
        data: isFormData ? data : (bodyStr || undefined),
        headers,
      });

      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 401) {
          throw new VoniAuthError('Invalid API Key or Signature');
        }
        throw new VoniApiError(
          `API Error: ${JSON.stringify(error.response.data)}`,
          error.response.status,
          error.response.data
        );
      }
      throw new VoniApiError((error as Error).message);
    }
  }
}
